import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { cachedGet, getCached } from '../lib/apiCache';

// Default cards used as a fallback when admin hasn't configured anything yet.
// These mirror the original hard-coded design (colors + image sizing).
const DEFAULT_CARDS = [
  {
    title: 'Unelte de grădină',
    titleRu: 'Садовые инструменты',
    description: 'Eficiență pentru fiecare sezon de grădinărit',
    descriptionRu: 'Эффективность для каждого сезона садоводства',
    buttonText: 'Vezi produse',
    buttonTextRu: 'Смотреть',
    url: '/category/unelte-de-gradina',
    image: 'https://i.postimg.cc/nrFnsTkG/Chat-GPT-Image-21-mai-2026-14-51-26.png',
    bgFrom: '#f7fee7', bgTo: '#dcfce7', accent: '#052e16', buttonColor: '#65a30d',
  },
  {
    title: 'Construcții și reparații',
    titleRu: 'Строительство и ремонт',
    description: 'Unelte puternice pentru lucrări durabile',
    descriptionRu: 'Мощные инструменты для надежных работ',
    buttonText: 'Vezi produse',
    buttonTextRu: 'Смотреть',
    url: '/category/constructii-si-reparatii',
    image: 'https://i.postimg.cc/hj1TMBjV/Chat-GPT-Image-21-mai-2026-12-34-54.png',
    bgFrom: '#fff7ed', bgTo: '#ffedd5', accent: '#ea580c', buttonColor: '#f97316',
  },
  {
    title: 'Irigare și accesorii',
    titleRu: 'Полив и аксессуары',
    description: 'Sisteme de irigare pentru culturi sănătoase',
    descriptionRu: 'Системы полива для здоровых культур',
    buttonText: 'Vezi produse',
    buttonTextRu: 'Смотреть',
    url: '/category/irigare-si-accesorii',
    image: 'https://i.postimg.cc/KzCFvFWB/image-XXXX.png',
    bgFrom: '#ecfdf5', bgTo: '#ccfbf1', accent: '#022c22', buttonColor: '#047857',
  },
  {
    title: 'Protecția plantelor',
    titleRu: 'Защита растений',
    description: 'Soluții eficiente pentru recolte bogate',
    descriptionRu: 'Эффективные решения для богатого урожая',
    buttonText: 'Vezi produse',
    buttonTextRu: 'Смотреть',
    url: '/category/protectia-plantelor',
    image: 'https://i.postimg.cc/x1pZS5h5/image-XXXX-(1).png',
    bgFrom: '#f5f3ff', bgTo: '#e0e7ff', accent: '#3730a3', buttonColor: '#4f46e5',
  },
];

const PromoCategoryCards = () => {
  const { language } = useLanguage();
  const isRu = language === 'ru';

  const [cards, setCards] = useState(() => {
    const cached = getCached('/api/settings');
    if (cached?.promoCards && cached.promoCards.length > 0) return cached.promoCards;
    return DEFAULT_CARDS;
  });

  useEffect(() => {
    cachedGet('/api/settings')
      .then((data) => {
        if (data?.promoCards && data.promoCards.length > 0) {
          setCards(data.promoCards);
        }
      })
      .catch(() => {});
  }, []);

  const sorted = [...cards].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <section
      className="w-full bg-white px-3 py-5 md:px-4 md:py-6"
      data-testid="promo-category-cards"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {sorted.map((card, idx) => {
          const title = isRu && card.titleRu ? card.titleRu : card.title;
          const description =
            isRu && card.descriptionRu ? card.descriptionRu : card.description;
          const buttonText =
            isRu && card.buttonTextRu ? card.buttonTextRu : card.buttonText;
          const baseUrl = card.url || '/catalog';
          const url = isRu && !baseUrl.startsWith('/ru') ? `/ru${baseUrl}` : baseUrl;

          const bgFrom = card.bgFrom || '#f7fee7';
          const bgTo = card.bgTo || '#dcfce7';
          const accent = card.accent || '#052e16';
          const btnColor = card.buttonColor || '#65a30d';

          return (
            <div
              key={idx}
              className="relative h-[185px] overflow-hidden rounded-[18px] px-6 py-6 ring-1 ring-black/5 md:h-[205px]"
              style={{
                background: `linear-gradient(135deg, ${bgFrom} 0%, ${bgTo} 100%)`,
              }}
              data-testid={`promo-card-${idx}`}
            >
              <div className="relative z-10 flex h-full max-w-[55%] flex-col items-start justify-between">
                <div>
                  <h3
                    className="text-[23px] font-black leading-[1.08] md:text-[25px]"
                    style={{ color: accent }}
                  >
                    {title}
                  </h3>

                  {description && (
                    <p className="mt-3.5 mb-4 text-[13px] font-semibold leading-[1.55] text-gray-600 md:mb-0 md:text-[14px]">
                      {description}
                    </p>
                  )}
                </div>

                {buttonText && (
                  <Link
                    to={url}
                    className="inline-flex rounded-full px-5 py-2.5 text-[13px] font-extrabold leading-none text-white"
                    style={{ backgroundColor: btnColor }}
                  >
                    {buttonText}
                  </Link>
                )}
              </div>

              {card.image && (
                <img
                  src={card.image}
                  alt={title || ''}
                  className="absolute z-0 object-contain pointer-events-none right-0 bottom-0 h-full w-[50%]"
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PromoCategoryCards;
